import React from 'react';
import { observer } from 'mobx-react';

// Helpers
import { flagPortrait, toHours } from '../../helpers/helpers.js';

// Translations
import translate from '../Translate.js';

// Dependencies
import { formatPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import moment from 'moment';
import DOMPurify from 'dompurify';

// Components
import * as icons from '../Icons/Icons';

const ProctorApproval = observer(class ProctorApproval extends React.Component {
  constructor() {
    super();
    this.toggleDescription = this.toggleDescription.bind(this);
    this.updateNote = this.updateNote.bind(this);
    this.renderNote = this.renderNote.bind(this);
    this.getLocations = this.getLocations.bind(this);
    this.approveProctor = this.approveProctor.bind(this);
  }

  componentWillMount() {
    this.state = {
      action: null,
      applyTo: null,
      showDescription: false,
      showLocation: false,
      gotLocations: false,
      submitted: false
    }
  }

  toggleDescription() {
    this.setState({
      showDescription: !this.state.showDescription
    })
  }

  updateNote() {
    const action = this.action.value;
    const applyTo = this.applyTo.value;

    this.setState({
      action,
      applyTo
    });
  }

  renderNote() {
    const _this = this;
    const { descriptions, descriptionsSubmitted } = this.props.translation.apply_to;

    function applyToWho() {
      switch (_this.state.applyTo) {
        case 'install':
          return descriptions.install;

        case 'instructor':
          return descriptions.instructor;

        case 'course':
          return descriptions.course;

        case 'exam':
          return descriptions.exam;

        case 'student':
          return descriptions.student;

        case 'enrollment':
          return descriptions.enrollment;

        case 'session':
          return descriptions.enrollment_exam;

        default:
          return '';
      }
    }

    if(this.state.applyTo && this.state.action) {
      if(!this.state.submitted) {
        return(
          <em className="actions__note">{this.state.action === 'allow' ? descriptions.approved : descriptions.denied} {applyToWho()}.</em>
        )
      } else {
        return(
          <em className="actions__note">{this.state.action === 'allow' ? descriptionsSubmitted.approved : descriptionsSubmitted.denied} {applyToWho()}.</em>
        )
      }
    }

    return '';
  }

  getLocations() {
    const { approval, store } = this.props;

    if(!this.state.gotLocations) {
      store.LocationStore.getLocations(approval.proctorAccount.sid)
    }

    if(!this.state.showLocation) {
      this.setState({
        showLocation: true,
        gotLocations: true
      });
    } else {
      this.setState({
        showLocation: false,
        gotLocations: true
      });
    }
  }

  approveProctor() {
    const { approval, store } = this.props;
    const { ProctorStore, SessionStore } = store;

    const data = {
      reviewedByUserSid: SessionStore.profile.sid,
      status: this.state.action,
      applyTo: this.state.applyTo
    }

    this.props.approvalCallback();

    ProctorStore.updatePendingApprovals(approval.sid, data);

    // Update the submission status
    this.setState({
      submitted: true
    })
  }

  renderVerificationData(data, now, i) {
    const { translation } = this.props;
    const { userTimezone } = this.props.store.SessionStore;
    const expireDate = moment(data.verificationExpires);
    const expireDiff = moment.duration(expireDate.diff(now)).asMinutes();

    if(expireDiff > 0) {
      return (
        <i className="approval__verified" aria-describedby={`${i}-${data.sid}-tooltip`}>
          <div className="tooltip tooltip--down">
            <span className="tooltip__label" role="presentation" aria-hidden="true">
              <icons.iconBadgeCheck />
            </span>

            <div id={`${i}-${data.sid}-tooltip`} className="tooltip__body" role="tooltip">
              <div className="tooltip__copy">
                {data.verificationNotes &&
                  <p
                    dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(data.verificationNotes)}}
                  ></p>
                }

                <p>
                  {translation.verified.replace(
                    '%date%',
                    moment(expireDate).tz(userTimezone).format('M/D/YYYY')
                  )}
                </p>
              </div>
            </div>
          </div>
        </i>
      )
    }

    return null;
  }

  render() {
    const { approval, proctor, store, translation } = this.props;
    const { SessionStore } = this.props.store;
    const { locations } = store.LocationStore;
    const phone = parsePhoneNumber(proctor.phoneNumber);
    const { createdDate, requestedByUser } = approval;
    const { course, exam, examSession, instructorUser } = approval.appliesTo;
    const { userTimezone } = this.props.store.SessionStore;
    const role = SessionStore.userData.role.toLowerCase();
    const now = moment(SessionStore.currentTime);

    return (
      <article className="approval">
        <div className="block">
          <header className="approval__header">
            <figure className="media">
              {proctor.image &&
                <span className="proctor__avatar avatar-wrap">
                  <img
                    className="avatar"
                    src={proctor.image}
                    alt=""
                    onLoad={(e) => {flagPortrait(e, proctor.image)}}
                  />
                </span>
              }

              {!proctor.image &&
                <span className="proctor__avatar avatar-wrap">
                  <img
                    className="avatar avatar--missing"
                    src="./static/img/avatar-default.png"
                    alt="No avatar provided"
                    onLoad={(e) => {flagPortrait(e, './static/img/avatar-default.png')}}
                  />
                </span>
              }

              <figcaption className="media__copy">
                <h3 className="approval__title fn org">
                  {proctor.firstName &&
                    `${proctor.firstName} ${proctor.lastName}`
                  }
                  {!proctor.firstName &&
                    proctor.accountName
                  }
                </h3>

                {proctor.firstName && proctor.accountName &&
                  <span className="approval__account">{proctor.accountName}</span>
                }

                {proctor.email &&
                  <a className="approval__email" href={`mailto:${proctor.email}`}>{proctor.email}</a>
                }
              </figcaption>
            </figure>
          </header>

          <div className="approval__details">
            {proctor.description &&
              <div className={`approval__description text-block ${this.state.showDescription ? 'visible' : ''}`}>
                <p>{proctor.description}</p>
              </div>
            }

            <section className="approval__details-section">
              <h4>Proctor Type</h4>

              {approval.proctorTypes &&
                <ul className="approval__category data--comma">
                  {approval.proctorTypes.map((proctorType, i) =>
                    <li key={i}>
                      {proctorType.title}

                      {proctorType.verificationCompletionDate &&
                        this.renderVerificationData(proctorType, now, i)
                      }
                    </li>
                  )}
                </ul>
              }
            </section>

            <section className="approval__details-section">
              <h4>Session Details</h4>

              <ul className="data--alt">
                <li>
                  <strong>{translation.session_details.requested_by}:</strong>
                  <p>{requestedByUser.firstName} {requestedByUser.lastName}</p>
                </li>
                <li>
                  <strong>{translation.session_details.requested_on}:</strong>
                  <p>{moment.utc(createdDate).tz(userTimezone).format('M/D/YYYY [at] h:mma')}</p>
                </li>
                {course &&
                  <li>
                    <strong>{translation.session_details.course}:</strong>
                    <p>{course.title} ({course.code})</p>
                  </li>
                }
                {instructorUser &&
                  <li>
                    <strong>{translation.session_details.instructor}:</strong>
                    <p>{instructorUser.firstName} {instructorUser.lastName}</p>
                  </li>
                }
                {exam &&
                  <li>
                    <strong>{translation.session_details.exam}:</strong>
                    <p>{exam.title}</p>
                  </li>
                }
                {examSession && examSession.examConfiguration && examSession.examConfiguration.duration &&
                  <li>
                    <strong>{translation.session_details.exam_duration}:</strong>
                    <p>{toHours(examSession.examConfiguration.duration, false, true, true)}</p>
                  </li>
                }
                {examSession && examSession.examConfiguration &&
                  <li>
                    <strong>{translation.session_details.exam_dates}:</strong>
                    <p>
                      {moment.utc(examSession.examConfiguration.examOpenDate).tz(userTimezone).format('M/D/YYYY [at] h:mma')}
                      –
                      {moment.utc(examSession.examConfiguration.examCloseDate).tz(userTimezone).format('M/D/YYYY [at] h:mma z')}
                    </p>
                  </li>
                }
                {examSession &&
                  <li>
                    <strong>{translation.session_details.appointment_date}:</strong>
                    {examSession.appointmentDate &&
                      <p>{moment.utc(examSession.appointmentDate).tz(userTimezone).format('M/D/YYYY [at] h:mma z')}</p>
                    }

                    {!examSession.appointmentDate &&
                      <p>Not scheduled</p>
                    }
                  </li>
                }
              </ul>
            </section>
          </div>

          <button className="approval__more" onClick={this.getLocations}>
            {!this.state.showLocation &&
              <span>
                <icons.iconPlus />
                {translation.more_information}
              </span>
            }

            {this.state.showLocation &&
              <span>
                <icons.iconMinus />
                {translation.less_information}
              </span>
            }
          </button>

          <footer className="approval__actions actions">
            {!this.state.submitted &&
              <div>
                <label className="select">
                  <select onChange={this.updateNote} ref={(input) => {this.action = input}}>
                    <option value="">{translation.action.label}</option>
                    <option value="allow">{translation.action.approve}</option>
                    <option value="deny">{translation.action.deny}</option>
                  </select>
                </label>

                <label className="select">
                  <select onChange={this.updateNote} ref={(input) => {this.applyTo = input}}>
                    <option value="">{translation.apply_to.label}</option>
                    {
                      Object
                        .keys(translation.apply_to[`${role}_options`])
                        .map(
                          (key) =>
                          <option key={key} value={key}>
                            {translation.apply_to[`${role}_options`][key]}
                          </option>
                        )
                    }
                  </select>
                </label>

                <button
                  className="actions__btn actions__btn--primary"
                  onClick={this.approveProctor}
                  disabled={!this.state.action || !this.state.applyTo}
                >
                  {translation.submit}
                </button>

                {this.renderNote()}
              </div>
            }

            {this.state.submitted &&
              this.renderNote()
            }
          </footer>
        </div>

        <div className={`approval__locations slideout ${this.state.showLocation ? 'open' : ''}`}>
          {locations.map(
            (key, i) =>
            <ul key={i} className="data vcard">
              <li>
                <div className="adr">
                  <strong className="subtitle">{key.name}</strong>

                  <span className="street-address">
                    {key.address1} {key.address2}
                  </span>

                  {key.place &&
                    <span className="locality">{key.place}, </span>
                  }
                  <span className="region">{key.region} </span>
                  <span className="postal-code">{key.postalCode}</span>
                </div>

                {proctor.phoneNumber &&
                  <div className="tel">
                    <span className="type">work</span>
                    <span className="value">
                      {formatPhoneNumber(phone, 'National')}
                    </span>
                  </div>
                }

                {key.parkingInformation &&
                  <div className="approval__directions">
                    <strong>{translation.parking}</strong>
                    <p>{key.parkingInformation}</p>
                  </div>
                }
              </li>
              {(
                key.hasComputerAvailable ||
                key.hasPrinterAvailable ||
                key.hasCopierAvailable ||
                key.hasFaxAvailable ||
                key.hasElevator ||
                key.hasWheelchairRamp ||
                key.hasAlternativeKeyboards ||
                key.hasScreenEnlarger ||
                key.hasScreenReader ||
                key.hasSpeechRecognition ||
                key.hasTextToSpeech ||
                key.hasDistractionFreeRooms
              ) &&
                <li>
                  <strong>{translation.features_heading}</strong>
                  <ul className="approval__features">
                    {key.hasComputerAvailable &&
                      <li>{translation.features.computer}</li>
                    }

                    {key.hasPrinterAvailable &&
                      <li>{translation.features.printer}</li>
                    }

                    {key.hasCopierAvailable &&
                      <li>{translation.features.copier}</li>
                    }

                    {key.hasFaxAvailable &&
                      <li>{translation.features.fax}</li>
                    }

                    {key.hasElevator &&
                      <li>{translation.features.elevator}</li>
                    }

                    {key.hasWheelchairRamp &&
                      <li>{translation.features.wheelchair_ramp}</li>
                    }

                    {key.hasAlternativeKeyboards &&
                      <li>{translation.features.alternative_keyboards}</li>
                    }

                    {key.hasScreenEnlarger &&
                      <li>{translation.features.screen_enlarger}</li>
                    }

                    {key.hasScreenReader &&
                      <li>{translation.features.screen_reader}</li>
                    }

                    {key.hasSpeechRecognition &&
                      <li>{translation.features.speech_recognition}</li>
                    }

                    {key.hasTextToSpeech &&
                      <li>{translation.features.text_to_speech}</li>
                    }

                    {key.hasDistractionFreeRooms &&
                      <li>{translation.features.distraction_free_rooms}</li>
                    }
                  </ul>
                </li>
              }
            </ul>
          )}
        </div>
      </article>
    )
  }
})

export default translate('ProctorApproval')(ProctorApproval);



// WEBPACK FOOTER //
// ./src/app/components/Approvals/ProctorApproval.js